import { expand } from './toggle';
import type { HierarchyPointNode } from 'd3';
import { splitName, visit } from './utils';
import type { TreeNode } from '../types';

/**
 * Creates the path to nodes for search.
 *
 * @param node - The current node in the hierarchy.
 * @param path - The path array to accumulate indices.
 * @returns The path to the node as an array of indices.
 */
function getNodePath(node: HierarchyPointNode<TreeNode>, path: number[] = []): number[] {
    const p = path || [];
    const parent = node.parent;
    if (parent) {
        const children = parent.children || parent.data._children;
        p.unshift(children.indexOf(node));
        return getNodePath(parent, p);
    } else {
        return p;
    }
}

/**
 * Sets up the search list.
 *
 * @param root - The root node of the hierarchy.
 * @returns An array of search strings with paths to the nodes.
 */
function createSearchList(root: HierarchyPointNode<TreeNode>): string[] {
    const searchList: string[] = [];
    visit(
        root,
        /**
         * Creates the search list with paths to the nodes.
         *
         * @param current - The current node in the hierarchy.
         */
        (current: HierarchyPointNode<TreeNode>) => {
            if (current) {
                searchList.push(current.data.name + '|' + getNodePath(current, []).join('-'));
            }
        },
        /**
         * Gets the children of the current node.
         *
         * @param current - The current node in the hierarchy.
         * @returns The children of the current node.
         */
        (current: HierarchyPointNode<TreeNode>) => {
            return current.children || current.data._children;
        }
    );
    // Removes name of floorplan from search list
    searchList.splice(searchList.indexOf(root.data.name + '|'), 1).sort((a, b) => a.localeCompare(b));
    return searchList;
}

/**
 * Gets a DOM element by its selector.
 *
 * @param selector - The CSS selector of the element.
 * @returns The DOM element.
 */
function getDOMElement(selector: string): HTMLElement {
    return document.querySelector(selector);
}

/**
 * Shows a DOM element by setting its display style to 'block'.
 *
 * @param element - The DOM element to show.
 */
function showDOMElement(element: HTMLElement): void {
    element.style.display = 'block';
}

/**
 * Hides a DOM element by setting its display style to 'none'.
 *
 * @param element - The DOM element to hide.
 */
function hideDOMElement(element: HTMLElement): void {
    element.style.display = 'none';
}

/**
 * Removes all child elements from a container and hides it.
 *
 * @param container - The container element.
 */
function removeDOMElements(container: HTMLElement): void {
    while (container.firstChild) {
        container.removeChild(container.firstChild);
    }
    hideDOMElement(container);
}

/**
 * Creates and appends a DOM element to a container.
 *
 * @param content - The content to set in the new element.
 * @param container - The container to append the new element to.
 */
function createDOMElements(content: string, container: HTMLElement): void {
    const listElement = document.createElement('li');
    listElement.setAttribute('data-path', content.split('|')[1]);
    listElement.textContent = splitName(content.split('|')[0]);
    container.appendChild(listElement);
    showDOMElement(container);
}

/**
 * Handles the search input event.
 *
 * @param items - The list of searchable items.
 * @param inputElement - The input element for search.
 * @param icon - The icon element to show or hide.
 * @param container - The container for search results.
 */
function searchInputEventHandler(
    items: string[],
    inputElement: HTMLInputElement,
    icon: HTMLElement,
    container: HTMLElement
): void {
    const searchValue = inputElement.value.toUpperCase().trim();
    if (searchValue === null || searchValue === '') {
        removeDOMElements(container);
        hideDOMElement(icon);
    } else {
        for (const item of items) {
            const listValue = item.split('|')[0].toUpperCase();
            if (listValue.indexOf(searchValue) >= 0 || listValue.replace(/\s/g, '').indexOf(searchValue) >= 0) {
                createDOMElements(item, container);
            }
            showDOMElement(icon);
        }
    }
}

/**
 * Expands the node path in the hierarchy.
 *
 * @param path - The path to expand as an array of indices.
 * @param rootNode - The root node of the hierarchy.
 * @returns The expanded node.
 */
export function expandNodePath(path: string[], rootNode: HierarchyPointNode<TreeNode>): HierarchyPointNode<TreeNode> {
    let node = rootNode; // Start with root
    for (const element of path) {
        if (node.data._children) {
            expand(node);
        }
        node = node.children[element];
    }
    return node;
}

/**
 * Sets up the in-built search functionality.
 *
 * @param treeData - The root node of the hierarchy.
 */
export function setupInBuiltSearch(treeData: HierarchyPointNode<TreeNode>): void {
    const searchInputElement = getDOMElement('#search-input') as HTMLInputElement;
    const searchResultContainer = getDOMElement('#search-result');
    const searchContainer = getDOMElement('#search-container');
    const searchIcon = getDOMElement('#search-icon');
    const searchCloseIcon = getDOMElement('#search-close-icon');
    if (!searchContainer) {
        return;
    } else {
        searchCloseIcon.addEventListener('click', () => {
            searchInputElement.value = null;
            removeDOMElements(searchResultContainer);
            hideDOMElement(searchCloseIcon);
        });
        searchContainer.addEventListener('focusin', () => {
            hideDOMElement(searchIcon);
        });
        searchContainer.addEventListener('focusout', () => {
            showDOMElement(searchIcon);
        });
        searchInputElement.addEventListener('input', () => {
            removeDOMElements(searchResultContainer);
            searchInputEventHandler(
                createSearchList(treeData),
                searchInputElement,
                searchCloseIcon,
                searchResultContainer
            );
        });
    }
}
